import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import AddressFields from "../FormComponents/AddressFields";
import {
  fetchAddressDetailsByParent,
  addAddressData,
  editAddressData,
  deleteAddressData,
} from "../../services/addressService";

// Helper function to decode HTML entities
const decodeHTMLEntities = (text) => {
  if (!text) return text;
  const textArea = document.createElement("textarea");
  textArea.innerHTML = text;
  return textArea.value;
};

/**
 * Add Address Dialog Component
 */
export const AddAddressDialog = ({
  open,
  onClose,
  sessionId,
  userId,
  onSuccess,
  onError,
  onAddressAdded,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [sameAsCurrent, setSameAsCurrent] = useState(false);

  const handleClear = () => {
    setFormData({
      current_address1: "",
      current_street1: "",
      current_pincode1: "",
      current_village1: "",
      current_taluk1: "",
      current_district1: "",
      current_state1: "",
      current_country1: "",
      permanent_address1: "",
      permanent_street1: "",
      permanent_pincode1: "",
      permanent_village1: "",
      permanent_taluk1: "",
      permanent_district1: "",
      permanent_state1: "",
      permanent_country1: "",
    });
    setFieldErrors({});
    setSameAsCurrent(false);
  };

  const validateForm = () => {
    const errors = {};
    // Add validation logic if needed
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      if (onError) {
        onError("Please fill in all required fields.");
      }
      return;
    }

    setIsLoading(true);
    try {
      // Save Current Address
      const currentAddressData = {
        addresstype_c: "Current",
        address: formData.current_address1,
        street: formData.current_street1,
        pincode: formData.current_pincode1,
        village: formData.current_village1,
        taluk: formData.current_taluk1,
        district: formData.current_district1,
        state: formData.current_state1,
        country: formData.current_country1,
        parentid: userId,
      };

      await addAddressData(sessionId, currentAddressData);

      // Save Permanent Address
      const permanentAddressData = {
        addresstype_c: "Permanent",
        address: formData.permanent_address1,
        street: formData.permanent_street1,
        pincode: formData.permanent_pincode1,
        village: formData.permanent_village1,
        taluk: formData.permanent_taluk1,
        district: formData.permanent_district1,
        state: formData.permanent_state1,
        country: formData.permanent_country1,
        parentid: userId,
      };

      await addAddressData(sessionId, permanentAddressData);

      // Fetch updated list
      const updatedAddressEntries = await fetchAddressDetailsByParent(sessionId, userId);

      if (onAddressAdded) {
        onAddressAdded(updatedAddressEntries);
      }

      handleClear();
      onClose();

      if (onSuccess) {
        onSuccess("Address details added successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to add address details. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      handleClear();
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle "Same as Current Address" checkbox
  const handleSameAsCurrentChange = (checked) => {
    setSameAsCurrent(checked);
    if (checked) {
      // Copy current address fields to permanent address fields
      setFormData((prev) => ({
        ...prev,
        permanent_address1: prev.current_address1,
        permanent_street1: prev.current_street1,
        permanent_pincode1: prev.current_pincode1,
        permanent_village1: prev.current_village1,
        permanent_taluk1: prev.current_taluk1,
        permanent_district1: prev.current_district1,
        permanent_state1: prev.current_state1,
        permanent_country1: prev.current_country1,
      }));
    }
  };

  // Update permanent address when current address changes and checkbox is checked
  React.useEffect(() => {
    if (sameAsCurrent) {
      setFormData((prev) => ({
        ...prev,
        permanent_address1: prev.current_address1,
        permanent_street1: prev.current_street1,
        permanent_pincode1: prev.current_pincode1,
        permanent_village1: prev.current_village1,
        permanent_taluk1: prev.current_taluk1,
        permanent_district1: prev.current_district1,
        permanent_state1: prev.current_state1,
        permanent_country1: prev.current_country1,
      }));
    }
  }, [
    sameAsCurrent,
    formData.current_address1,
    formData.current_street1,
    formData.current_pincode1,
    formData.current_village1,
    formData.current_taluk1,
    formData.current_district1,
    formData.current_state1,
    formData.current_country1,
  ]);

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="info" sx={{ mr: 1 }}>
              home
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Add Address Details
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          {/* Current Address Section */}
          <MDTypography variant="h6" fontWeight="medium" color="info" mb={2}>
            📍 Current Address
          </MDTypography>
          <AddressFields
            formData={formData}
            handleChange={handleChange}
            addressType="Current"
            section={1}
            textInputStyle={{}}
            fieldErrors={fieldErrors}
          />

          <Divider sx={{ my: 4 }} />

          {/* Permanent Address Section */}
          <MDTypography variant="h6" fontWeight="medium" color="secondary" mb={2}>
            🏠 Permanent Address
          </MDTypography>
          <AddressFields
            formData={formData}
            handleChange={handleChange}
            addressType="Permanent"
            section={1}
            textInputStyle={{}}
            fieldErrors={fieldErrors}
            onSameAsCurrentChange={handleSameAsCurrentChange}
            isSameAsCurrent={sameAsCurrent}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={handleClear} variant="outlined" color="warning" disabled={isLoading}>
          Clear
        </MDButton>
        <MDButton onClick={handleDialogClose} variant="outlined" color="dark" disabled={isLoading}>
          Cancel
        </MDButton>
        <MDButton onClick={handleSave} variant="gradient" color="info" disabled={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Adding...
            </>
          ) : (
            "Add Address"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Edit Address Dialog Component
 */
export const EditAddressDialog = ({
  open,
  onClose,
  currentAddress,
  permanentAddress,
  sessionId,
  userId,
  onSuccess,
  onError,
  onAddressUpdated,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [sameAsCurrent, setSameAsCurrent] = useState(false);

  // Load address data when dialog opens
  React.useEffect(() => {
    if (open && (currentAddress || permanentAddress)) {
      const newFormData = {
        current_address1: currentAddress ? decodeHTMLEntities(currentAddress.address) || "" : "",
        current_street1: currentAddress ? decodeHTMLEntities(currentAddress.street) || "" : "",
        current_pincode1: currentAddress ? currentAddress.pincode || "" : "",
        current_village1: currentAddress ? decodeHTMLEntities(currentAddress.village) || "" : "",
        current_taluk1: currentAddress ? decodeHTMLEntities(currentAddress.taluk) || "" : "",
        current_district1: currentAddress ? decodeHTMLEntities(currentAddress.district) || "" : "",
        current_state1: currentAddress ? decodeHTMLEntities(currentAddress.state) || "" : "",
        current_country1: currentAddress ? decodeHTMLEntities(currentAddress.country) || "" : "",
        permanent_address1: permanentAddress ? decodeHTMLEntities(permanentAddress.address) || "" : "",
        permanent_street1: permanentAddress ? decodeHTMLEntities(permanentAddress.street) || "" : "",
        permanent_pincode1: permanentAddress ? permanentAddress.pincode || "" : "",
        permanent_village1: permanentAddress ? decodeHTMLEntities(permanentAddress.village) || "" : "",
        permanent_taluk1: permanentAddress ? decodeHTMLEntities(permanentAddress.taluk) || "" : "",
        permanent_district1: permanentAddress ? decodeHTMLEntities(permanentAddress.district) || "" : "",
        permanent_state1: permanentAddress ? decodeHTMLEntities(permanentAddress.state) || "" : "",
        permanent_country1: permanentAddress ? decodeHTMLEntities(permanentAddress.country) || "" : "",
      };
      setFormData(newFormData);
      setFieldErrors({});

      // Check if addresses are the same
      if (currentAddress && permanentAddress) {
        const isSame =
          currentAddress.address === permanentAddress.address &&
          currentAddress.street === permanentAddress.street &&
          currentAddress.pincode === permanentAddress.pincode &&
          currentAddress.village === permanentAddress.village &&
          currentAddress.taluk === permanentAddress.taluk &&
          currentAddress.district === permanentAddress.district &&
          currentAddress.state === permanentAddress.state &&
          currentAddress.country === permanentAddress.country;
        setSameAsCurrent(isSame);
      }
    }
  }, [open, currentAddress, permanentAddress]);

  const validateForm = () => {
    const errors = {};
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      if (onError) {
        onError("Please fill in all required fields.");
      }
      return;
    }

    setIsLoading(true);
    try {
      // Update Current Address
      if (currentAddress && currentAddress.id) {
        const currentAddressData = {
          address: formData.current_address1,
          street: formData.current_street1,
          pincode: formData.current_pincode1,
          village: formData.current_village1,
          taluk: formData.current_taluk1,
          district: formData.current_district1,
          state: formData.current_state1,
          country: formData.current_country1,
          parentid: userId,
        };
        await editAddressData(sessionId, currentAddress.id, currentAddressData);
      }

      // Update Permanent Address
      if (permanentAddress && permanentAddress.id) {
        const permanentAddressData = {
          address: formData.permanent_address1,
          street: formData.permanent_street1,
          pincode: formData.permanent_pincode1,
          village: formData.permanent_village1,
          taluk: formData.permanent_taluk1,
          district: formData.permanent_district1,
          state: formData.permanent_state1,
          country: formData.permanent_country1,
          parentid: userId,
        };
        await editAddressData(sessionId, permanentAddress.id, permanentAddressData);
      }

      // Fetch updated list
      const updatedAddressEntries = await fetchAddressDetailsByParent(sessionId, userId);

      if (onAddressUpdated) {
        onAddressUpdated(updatedAddressEntries);
      }

      onClose();

      if (onSuccess) {
        onSuccess("Address details updated successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to update address details. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSameAsCurrentChange = (checked) => {
    setSameAsCurrent(checked);
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        permanent_address1: prev.current_address1,
        permanent_street1: prev.current_street1,
        permanent_pincode1: prev.current_pincode1,
        permanent_village1: prev.current_village1,
        permanent_taluk1: prev.current_taluk1,
        permanent_district1: prev.current_district1,
        permanent_state1: prev.current_state1,
        permanent_country1: prev.current_country1,
      }));
    }
  };

  React.useEffect(() => {
    if (sameAsCurrent) {
      setFormData((prev) => ({
        ...prev,
        permanent_address1: prev.current_address1,
        permanent_street1: prev.current_street1,
        permanent_pincode1: prev.current_pincode1,
        permanent_village1: prev.current_village1,
        permanent_taluk1: prev.current_taluk1,
        permanent_district1: prev.current_district1,
        permanent_state1: prev.current_state1,
        permanent_country1: prev.current_country1,
      }));
    }
  }, [
    sameAsCurrent,
    formData.current_address1,
    formData.current_street1,
    formData.current_pincode1,
    formData.current_village1,
    formData.current_taluk1,
    formData.current_district1,
    formData.current_state1,
    formData.current_country1,
  ]);

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="primary" sx={{ mr: 1 }}>
              edit
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Edit Address Details
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          {/* Current Address Section */}
          <MDTypography variant="h6" fontWeight="medium" color="info" mb={2}>
            📍 Current Address
          </MDTypography>
          <AddressFields
            formData={formData}
            handleChange={handleChange}
            addressType="Current"
            section={1}
            textInputStyle={{}}
            fieldErrors={fieldErrors}
          />

          <Divider sx={{ my: 4 }} />

          {/* Permanent Address Section */}
          <MDTypography variant="h6" fontWeight="medium" color="secondary" mb={2}>
            🏠 Permanent Address
          </MDTypography>
          <AddressFields
            formData={formData}
            handleChange={handleChange}
            addressType="Permanent"
            section={1}
            textInputStyle={{}}
            fieldErrors={fieldErrors}
            onSameAsCurrentChange={handleSameAsCurrentChange}
            isSameAsCurrent={sameAsCurrent}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={handleDialogClose} variant="outlined" color="dark" disabled={isLoading}>
          Cancel
        </MDButton>
        <MDButton onClick={handleSave} variant="gradient" color="info" disabled={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            "Update"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Delete Address Confirmation Dialog Component
 */
export const DeleteAddressDialog = ({
  open,
  onClose,
  currentAddress,
  permanentAddress,
  sessionId,
  userId,
  onSuccess,
  onError,
  onAddressDeleted,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      // Delete current address if exists
      if (currentAddress && currentAddress.id) {
        await deleteAddressData(sessionId, currentAddress.id);
      }

      // Delete permanent address if exists
      if (permanentAddress && permanentAddress.id) {
        await deleteAddressData(sessionId, permanentAddress.id);
      }

      // Fetch updated list
      const updatedAddressEntries = await fetchAddressDetailsByParent(sessionId, userId);

      if (onAddressDeleted) {
        onAddressDeleted(updatedAddressEntries);
      }

      onClose();

      if (onSuccess) {
        onSuccess("Address details deleted successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to delete address details. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center">
          <Icon color="error" sx={{ mr: 1 }}>
            warning
          </Icon>
          <MDTypography variant="h6" fontWeight="bold">
            Confirm Deletion
          </MDTypography>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text">
          Are you sure you want to delete these address details?
        </MDTypography>
        {(currentAddress || permanentAddress) && (
          <MDBox mt={2} p={2} sx={{ backgroundColor: "grey.100", borderRadius: 1 }}>
            {currentAddress && (
              <>
                <MDTypography variant="body2" fontWeight="medium">
                  Current Address: {decodeHTMLEntities(currentAddress.address) || "N/A"}
                </MDTypography>
                <MDTypography variant="body2">
                  {decodeHTMLEntities(currentAddress.village)}, {decodeHTMLEntities(currentAddress.district)},{" "}
                  {decodeHTMLEntities(currentAddress.state)} - {currentAddress.pincode}
                </MDTypography>
              </>
            )}
            {permanentAddress && (
              <>
                <MDTypography variant="body2" fontWeight="medium" mt={1}>
                  Permanent Address: {decodeHTMLEntities(permanentAddress.address) || "N/A"}
                </MDTypography>
                <MDTypography variant="body2">
                  {decodeHTMLEntities(permanentAddress.village)}, {decodeHTMLEntities(permanentAddress.district)},{" "}
                  {decodeHTMLEntities(permanentAddress.state)} - {permanentAddress.pincode}
                </MDTypography>
              </>
            )}
          </MDBox>
        )}
        <MDTypography variant="caption" color="error" mt={2} display="block">
          This action cannot be undone.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleDialogClose} variant="outlined" color="dark" disabled={isLoading}>
          Cancel
        </MDButton>
        <MDButton onClick={handleConfirm} variant="gradient" color="error" disabled={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Deleting...
            </>
          ) : (
            "Delete"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
